import { createSSRApp, nextTick } from "vue";
import {
  createRouter,
  createWebHistory,
  createMemoryHistory,
} from "vue-router";
import routes from "voie-pages";
import Cookie from "@vuefront-cookie";
import isUndefined from "lodash/isUndefined";
import merge from "lodash/merge";
import { createScrollRestoreGuard } from "~/core/utils/scrollRestoreGuard";

import { getRoutes } from "@vuefront-routes";
import VuefrontI18n from "@vuefront-i18n";
import VuefrontApollo from "@vuefront-apollo";
import VuefrontClient from "@vuefront-client";
import VuefrontStore from "@vuefront-store";
import VuefrontPlugin from "@vuefront-plugin";
const VfTSeoResolver = () =>
  import("~/core/components/templates/common/seo-resolver/seo-resolver.vue");
import {
  isInitialDocumentBackForwardNavigation,
  isQueryOnlyNavigation,
  shouldRestoreSavedScroll,
} from "~/core/utils/navigationTransition";
import {
  clearCatalogHistoryVisit,
  consumeCatalogPageScrollClaim,
} from "~/core/utils/catalogPageScroll";
export const createVueFrontApp = async (App, runtimeContext = {}) => {
  const app = {
    ...App,
  };
  let context = { ...app, ...runtimeContext };
  let vuefrontApp = null;

  let injectVars = {};

  let store = null;

  const inject = (key, value) => {
    if (isUndefined(app[key])) {
      key = "$" + key;
      context[key] = value;
      app[key] = value;
      store[key] = value;
      injectVars[key] = value;
      context.app.config.globalProperties[key] = value;
      context.app.provide(key, value);
    }
  };
  context.router = null;

  let i18n = null;
  let router = null;
  // Holds last known stable scroll positions keyed by route fullPath.
  // Used to restore scroll reliably even if internal router logic overrides history.state.scroll.
  const savedScrollByPath = new Map();
  // A path can appear more than once in history. Keep an entry-keyed copy as
  // the authoritative source for Back/Forward, with the path map as fallback.
  const savedScrollByEntry = new Map();
  const historyPosition = () => {
    if (typeof window === "undefined") return null;
    const position = Number(window.history?.state?.position);
    return Number.isFinite(position) ? position : null;
  };
  let activeHistoryPosition = historyPosition();
  let hasCompletedInitialNavigation = false;
  const hasEarlyHistoryNavigation = () =>
    typeof window !== "undefined" && !!window.__vfEarlyHistoryNavigation;
  const RESTORE_CATALOG_SCROLL_KEY = "vf_restore_catalog_scroll";

  const isCatalogLikePath = (path) =>
    path === "/" ||
    [
      "/store/category",
      "/store/manufacturer",
      "/store/search",
      "/store/special",
      "/store/catalog",
    ].some((prefix) => path.includes(prefix));

  const isLeavingCatalogRoute = (from) => {
    if (!from?.path) return false;
    if (isCatalogLikePath(from.path)) return true;

    return (
      store?.state?.vuefront?.resolvedSeoRoute?.type === "category" &&
      from.name === "_slug"
    );
  };

  vuefrontApp = createSSRApp({
    i18n,
    store,
    router,
    ...app,
    ...injectVars,
  });
  context.app = vuefrontApp;
  router = createRouter({
    history: import.meta.env.SSR ? createMemoryHistory() : createWebHistory(),
    routes: [
      ...routes,
      ...getRoutes(context),
      {
        name: "_slug",
        path: "/:slug/:slug2?/:slug3?",
        component: VfTSeoResolver,
      },
    ],
    scrollBehavior(to, from, savedPosition) {
      // Consumed (and cleared) on every navigation, so a category's claim for
      // its View Next/Previous push can never reach another navigation.
      const catalogOwnsScroll = consumeCatalogPageScrollClaim(to, from);
      const restoreWhenReady = (desiredSaved) => {
        return new Promise((resolve) => {
          if (typeof window === "undefined") {
            resolve(desiredSaved);
            return;
          }

          const guard = createScrollRestoreGuard();
          const finish = () => {
            guard.dispose();
            isPopNavigation = false;
            isBFCacheRestore = false;
            window.__vfEarlyHistoryNavigation = null;
            // We applied the position ourselves. Avoid a second router scroll.
            resolve(false);
          };
          const run = async () => {
            await nextTick();

            let attempts = 0;
            const maxAttempts = 120;

            const tryRestore = () => {
              if (!guard.isActive()) {
                finish();
                return;
              }

              const fullHeight = document.documentElement.scrollHeight;
              const viewport = window.innerHeight;
              const targetTop = desiredSaved.top ?? 0;
              const reachedTarget =
                Math.abs(window.scrollY - targetTop) <= 4 ||
                fullHeight >= targetTop + viewport;

              if (reachedTarget || attempts >= maxAttempts) {
                window.scrollTo({
                  left: desiredSaved.left ?? 0,
                  top: desiredSaved.top ?? 0,
                  behavior: "instant",
                });
                finish();
                return;
              }

              attempts++;
              window.requestAnimationFrame(tryRestore);
            };

            window.requestAnimationFrame(tryRestore);
          };

          void run().catch(finish);
        });
      };

      const clearHistorySignals = () => {
        isPopNavigation = false;
        isBFCacheRestore = false;
        if (typeof window !== "undefined") {
          window.__vfEarlyHistoryNavigation = null;
        }
      };
      // The category owns this scroll: its View Next/Previous push lands at
      // the catalog top, and its Back/Forward page visit returns to the saved
      // position once the page is on screen. Decided before the history
      // checks below: this listener's popstate flag runs after vue-router's,
      // so it can still be set from an earlier visit.
      if (catalogOwnsScroll) {
        queueMicrotask(clearHistorySignals);
        return false;
      }

      const mappedScroll = savedScrollByPath.get(to.fullPath);
      const mappedEntryScroll = savedScrollByEntry.get(historyPosition());
      const perfNav =
        typeof performance !== "undefined" &&
        performance.getEntriesByType &&
        performance.getEntriesByType("navigation");
      const navType = perfNav && perfNav[0]?.type;
      const isBackForwardNavigation =
        isPopNavigation ||
        isBFCacheRestore ||
        hasEarlyHistoryNavigation() ||
        isInitialDocumentBackForwardNavigation(
          navType,
          hasCompletedInitialNavigation
        );

      if (isBackForwardNavigation) {
        if (mappedEntryScroll) {
          return restoreWhenReady(mappedEntryScroll);
        }

        if (shouldRestoreSavedScroll(isBackForwardNavigation, savedPosition)) {
          return restoreWhenReady(savedPosition);
        }

        if (mappedScroll) {
          return restoreWhenReady(mappedScroll);
        }
      }

      if (to.hash) {
        const reduceMotion =
          typeof window !== "undefined" &&
          window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        queueMicrotask(() => {
          isPopNavigation = false;
          isBFCacheRestore = false;
          if (typeof window !== "undefined") {
            window.__vfEarlyHistoryNavigation = null;
          }
        });
        return { el: to.hash, behavior: reduceMotion ? "auto" : "smooth" };
      }

      queueMicrotask(clearHistorySignals);
      return { top: 0 };
    },
    fallback: false,
  });

  let isPopNavigation = false;
  let isBFCacheRestore = false;

  if (typeof window !== "undefined") {
    window.addEventListener("popstate", () => {
      isPopNavigation = true;
    });

    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        isBFCacheRestore = true;
        // BFCache restores the document and its scroll before `pageshow`.
        // Do not leave this transient signal set for the user's next ordinary
        // router.push (for example, the next product-card click).
        queueMicrotask(() => {
          isBFCacheRestore = false;
        });
      }
    });
  }

  router.beforeEach((to, from, next) => {
    // entry-client's popstate listener runs before vue-router's and sets
    // this signal; without it the navigation is not a Back/Forward visit,
    // so a listing must not treat it as one.
    if (!hasEarlyHistoryNavigation()) clearCatalogHistoryVisit();
    if (typeof window !== "undefined" && window.history) {
      try {
        const currentScrollPosition = {
          left: window.pageXOffset,
          top: window.pageYOffset,
        };
        const isForwardNavigation =
          !isPopNavigation && !isBFCacheRestore && !hasEarlyHistoryNavigation();

        if (isForwardNavigation) {
          // Persist native history state when Vue Router has created one. The
          // path/entry maps are saved regardless, because history.state can be
          // null in embedded/WebView environments.
          if (window.history.state) {
            window.history.replaceState(
              { ...window.history.state, scroll: currentScrollPosition },
              document.title
            );
          }

          if (from && from.fullPath) {
            savedScrollByPath.set(from.fullPath, currentScrollPosition);
          }
          const entryPosition = activeHistoryPosition ?? historyPosition();
          if (entryPosition !== null) {
            savedScrollByEntry.set(entryPosition, currentScrollPosition);
          }

          if (
            isLeavingCatalogRoute(from) &&
            typeof sessionStorage !== "undefined"
          ) {
            sessionStorage.setItem("vf_last_catalog_url", from.fullPath);
            sessionStorage.setItem(
              RESTORE_CATALOG_SCROLL_KEY,
              String(currentScrollPosition.top)
            );
          }
        }
      } catch (error) {
        // Ignore restricted replaceState environments
      }

      // Instant scroll-to-top on forward navigation to non-catalog pages.
      // Prevents a split-second footer flash before the router's async
      // scrollBehavior fires.
      if (
        !isPopNavigation &&
        !isBFCacheRestore &&
        !hasEarlyHistoryNavigation() &&
        !isCatalogLikePath(to.path) &&
        !isQueryOnlyNavigation(from?.path || "", to.path) &&
        !to.hash
      ) {
        window.scrollTo(0, 0);
      }
    }

    next();
  });

  router.afterEach(() => {
    activeHistoryPosition = historyPosition();
    hasCompletedInitialNavigation = true;
  });

  context.$router = router;
  context.app.use(router);
  context = {
    ...context,
    get $route() {
      return router.currentRoute;
    },
  };

  store = VuefrontStore(context, inject);
  store.app = context.app;
  context.app.use(store);
  store.$router = router;

  context.$store = store;
  context.store = store;

  // Hydrate the store with state captured during SSR (injected by server.js
  // as window.__syncState__). This lets pages render instantly from
  // server-fetched data instead of refetching on the client.
  if (
    typeof window !== "undefined" &&
    window.__syncState__ &&
    window.__syncState__.vuex
  ) {
    try {
      store.replaceState(merge({}, store.state, window.__syncState__.vuex));
    } catch (e) {
      const message = String(e?.message || e || "Unknown error")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 512);
      console.error("[hydrate] Failed to restore SSR state:", message);
    }
  }
  await VuefrontApollo(context, inject);
  const cookies = Cookie(runtimeContext.ssrRequest, runtimeContext.ssrResponse);
  inject("cookies", cookies);

  await VuefrontPlugin(context, inject);
  i18n = await VuefrontI18n(context, inject);
  inject("i18n", i18n);
  store.app.i18n = i18n;

  if (typeof document === "undefined") {
    await store.dispatch("vuefront/nuxtServerInit", app);
  } else if (typeof document !== "undefined") {
    await store.dispatch("vuefront/nuxtClientInit", app);
  }
  await VuefrontClient(context);

  if (typeof window !== "undefined") {
    window.__VUEFRONT__ = context;
  }
  return { app: vuefrontApp, router };
};
